import { FollowButtonOutline, FollowButtonSize } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ArticleTrustLabel } from "~/components/article/article-trust-label";
import { AuthorHeaderBar } from "~/components/author/author-header-bar";
import type { TooltipProps } from "~/components/follow-button/follow-tooltip";
import type { articleAuthorBlockArticle$key } from "~/queries/__generated__/articleAuthorBlockArticle.graphql";

import { ArticleDate } from "./article-date";
import { StyledEntityFollowButton } from "./author-names/styles";
import {
  ArticleAuthorBlockContainer,
  ArticleDateAndLabelContainer,
  AvatarContainer,
  InformationContainer,
  StyledAuthorAvatar,
  StyledAuthorNames,
} from "./styles";

export type Props = {
  className?: string;
  reference: articleAuthorBlockArticle$key;
  tooltipProps?: TooltipProps;
};

export const ArticleAuthorBlock: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  tooltipProps,
}) => {
  const article = useFragment(
    graphql`
      fragment articleAuthorBlockArticle on Article {
        ...articleDateContent
        ...authorNamesArticle
        authors {
          entityId
          ...entityFollowButtonBase
          ...authorAvatarAuthor
        }
      }
    `,
    reference_,
  );

  if (!article) return null;
  const authorsLength = article.authors?.length ?? 0;

  const handleRenderFollowButton = () => {
    const firstAuthor = first(article?.authors);
    if (!firstAuthor || authorsLength > 1) return null;
    return (
      <StyledEntityFollowButton
        outline={FollowButtonOutline.Outlined}
        reference={firstAuthor}
        size={FollowButtonSize.Thin}
        tooltipProps={tooltipProps}
      />
    );
  };

  return (
    <ArticleAuthorBlockContainer $authorsLength={authorsLength} className={className}>
      <AvatarContainer>
        {article.authors?.map(
          (author, index) =>
            author &&
            index < 6 && (
              <StyledAuthorAvatar
                $zIndex={authorsLength - index}
                height={60}
                key={author.entityId}
                reference={author}
                width={60}
              />
            ),
        )}
      </AvatarContainer>

      <InformationContainer $authorsLength={authorsLength}>
        <AuthorHeaderBar />
        <StyledAuthorNames reference={article}>{handleRenderFollowButton()}</StyledAuthorNames>
        <ArticleDateAndLabelContainer>
          <ArticleDate reference={article} />
          <ArticleTrustLabel />
        </ArticleDateAndLabelContainer>
      </InformationContainer>
    </ArticleAuthorBlockContainer>
  );
};

ArticleAuthorBlock.displayName = "ArticleAuthorBlock";
